import { computed, get } from "@ember/object"
import PromiseProxyMixin from "@ember/object/promise-proxy-mixin"
import ObjectProxy from "@ember/object/proxy"
import _ from "lodash"

/**
 * A macro that returns a computed property that returns an array of proxy objects
 * of the type specified in the relationshipType parameter

 * @param  {String}  modelName - string of model/object to look within in the JSON:API
 * @param  {String}  relationshipType - type of object to be returned from CP
 * @param  {String}  relationship - string of key to look for in relationship object of modelName object in JSON:API
 * @return {Array<PromiseProxyObject>} - an array of promiseProxyObjects that can be accessed
 * via the content property. Content will be null if no fetch request has been made.
 */
export default function hasMany(modelName: string, relationshipType: string, relationship: string) {
  /**
   * Example

    ``` javascript
      class exampleComponent extends Component {
        @hasMany("model", "course", "deletedCourses") deletedCourses
    }
    ```

      @return {[Array<PromiseProxyObject>]} an array of promiseProxyObjects of courses that have been deleted.
      Each course can be accessed via the content property. Content will be null if no fetch request has been made.
   */

  return computed(`${modelName}.relationships.${relationship}.data.@each.id`, function () {
    const ObjectPromiseProxy = ObjectProxy.extend(PromiseProxyMixin)
    if (get(this, "store") === null)
      throw new Error("You need to inject the store before using hasMany: " + this.toString())
    // line all ids up in one array
    let ids = _.chain(get(this, `${modelName}.relationships.${relationship}.data`))
      .map((r) => get(r, "id"))
      .reject((id) => id === "" || id === null)
      .value()

    return _.map(ids, (id) => {
      return ObjectPromiseProxy.create({
        promise: get(this, "store").find(relationshipType, id),
      })
    })
  })
}
